/**
 * Title Formatting Utilities
 * Converts O*NET plural titles to singular and improves display formatting
 */

/**
 * Convert plural O*NET titles to singular form
 * O*NET uses plural titles (e.g., "Lighting Technicians") but we want singular ("Lighting Technician")
 */
export function singularizeTitle(title: string): string {
  if (!title) return title;
  
  // Common plural endings to singularize
  const pluralRules: [RegExp, string][] = [
    // "Technicians" -> "Technician"
    [/ians$/i, 'ian'],
    // "Engineers" -> "Engineer"  
    [/eers$/i, 'eer'],
    // "Managers" -> "Manager"
    [/gers$/i, 'ger'],
    // "Workers" -> "Worker"
    [/kers$/i, 'ker'],
    // "Operators" -> "Operator"
    [/tors$/i, 'tor'],
    // "Specialists" -> "Specialist"
    [/ists$/i, 'ist'],
    // "Assistants" -> "Assistant"
    [/ants$/i, 'ant'],
    // "Representatives" -> "Representative"
    [/ives$/i, 'ive'],
    // "Analysts" -> "Analyst"
    [/ysts$/i, 'yst'],
    // "Nurses" -> "Nurse"
    [/ses$/i, 'se'],
    // "Drivers" -> "Driver"
    [/vers$/i, 'ver'],
    // "Clerks" -> "Clerk"
    [/rks$/i, 'rk'],
    // "Aides" -> "Aide"
    [/des$/i, 'de'],
    // "Attendants" -> "Attendant"
    [/nts$/i, 'nt'],
    // "Collectors" -> "Collector"
    [/ors$/i, 'or'],
    // "Installers" -> "Installer"
    [/ers$/i, 'er'],
    // Generic "s" ending (last resort)
    [/s$/i, ''],
  ];

  // Special cases that shouldn't be singularized
  const exceptions = [
    'Sales', // "Sales Representative" not "Sale Representative"
    'News',
    'Business',
    'Fitness',
    'Wellness',
    'Communications',
    'Operations',
    'Statistics',
    'Mathematics',
    'Physics',
    'Economics',
    'Electronics',
    'Logistics',
    'Graphics',
  ];

  // Check if the last word is an exception
  const words = title.split(' ');
  const lastWord = words[words.length - 1];
  
  if (exceptions.some(exc => lastWord.toLowerCase() === exc.toLowerCase())) {
    return title;
  }

  // Apply singularization to the last word only
  for (const [pattern, replacement] of pluralRules) {
    if (pattern.test(lastWord)) {
      words[words.length - 1] = lastWord.replace(pattern, replacement);
      return words.join(' ');
    }
  }

  return title;
}

/**
 * Format a career title for display
 * - Singularizes plural titles
 * - Ensures proper capitalization
 */
export function formatCareerTitle(title: string): string {
  if (!title) return title;
  
  // First singularize
  let formatted = singularizeTitle(title);
  
  // Ensure title case (first letter of each word capitalized)
  formatted = formatted
    .split(' ')
    .map(word => {
      // Keep small words lowercase unless first word
      const smallWords = ['and', 'or', 'the', 'a', 'an', 'of', 'in', 'for', 'to'];
      if (smallWords.includes(word.toLowerCase())) {
        return word.toLowerCase();
      }
      return word.charAt(0).toUpperCase() + word.slice(1);
    })
    .join(' ');
  
  // Ensure first letter is always capitalized
  return formatted.charAt(0).toUpperCase() + formatted.slice(1);
}
